export type MockNotificationType = 'BILLING' | 'DEALS';

export interface MockNotification {
  notificationId: number;
  notificationType: MockNotificationType;
  title: string;
  body: string;
  notificationLink: string;
  createdAt: number; // epoch ms
  isRead: boolean;
}

export interface GetNotificationsRequest {
  offset: number;
  limit: number;
  notificationType?: MockNotificationType | null;
}

export interface GetNotificationsResponse {
  notifications: MockNotification[];
  totalCount: number;
  unreadCount: number;
}

export interface MarkNotificationAsReadRequest {
  notificationId: number;
}

export interface MarkAllNotificationsAsReadResponse {
  updatedCount: number;
}

export interface DeleteNotificationRequest {
  notificationId: number;
}

const LATENCY_MS = 400;
const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), LATENCY_MS));

function seedNotifications(): MockNotification[] {
  const items: MockNotification[] = [];
  const now = Date.now();
  const HOUR = 3_600_000;
  for (let i = 0; i < 23; i += 1) {
    const type: MockNotificationType = i % 2 === 0 ? 'BILLING' : 'DEALS';
    items.push({
      notificationId: i + 1,
      notificationType: type,
      title: `Notification ${i + 1}`,
      body: `This is the detail body for notification ${
        i + 1
      }. It explains what happened and why you are seeing it.`,
      notificationLink: `/portal/notifications/${i + 1}`,
      createdAt: now - i * 5 * HOUR,
      isRead: i % 3 === 0,
    });
  }
  return items;
}

let store: MockNotification[] = seedNotifications();

function countUnread(): number {
  return store.filter((n) => !n.isRead).length;
}

// 1. getNotifications - paginated fetch, optionally filtered by type.
export function getNotifications({
  offset,
  limit,
  notificationType,
}: GetNotificationsRequest): Promise<GetNotificationsResponse> {
  const filtered = notificationType
    ? store.filter((n) => n.notificationType === notificationType)
    : store;
  const page = filtered.slice(offset, offset + limit);
  return delay({
    notifications: page,
    totalCount: filtered.length,
    unreadCount: countUnread(),
  });
}

// 2. markNotificationAsRead - empty response, mirrors real RPC.
export function markNotificationAsRead({
  notificationId,
}: MarkNotificationAsReadRequest): Promise<Record<string, never>> {
  store = store.map((n) =>
    n.notificationId === notificationId ? { ...n, isRead: true } : n
  );
  return delay({});
}

// 3. markAllNotificationsAsRead - returns how many were flipped.
export function markAllNotificationsAsRead(): Promise<MarkAllNotificationsAsReadResponse> {
  const updatedCount = countUnread();
  store = store.map((n) => (n.isRead ? n : { ...n, isRead: true }));
  return delay({ updatedCount });
}

// 4. deleteNotification - removes from the store.
export function deleteNotification({
  notificationId,
}: DeleteNotificationRequest): Promise<Record<string, never>> {
  store = store.filter((n) => n.notificationId !== notificationId);
  return delay({});
}

// Test helper: reset the in-memory store between specs.
export function __resetMockNotifications(): void {
  store = seedNotifications();
}
